/**
 * 預約加入行事曆
 *
 * 事件內容（標題、說明、地點、起訖時間）由後端 /get 的 calendar 提供，與 .ics 端點同一份，前台不自己組。
 *
 * 依裝置決定行為：
 *   - iPhone / iPad：直接開後端的 .ics（book/ics/{token}.ics），iOS 會跳出原生的「加入行事曆」畫面，
 *     使用者也可以在那個畫面選同步到手機的 Google、Outlook 帳號
 *   - Android：用 intent 直接開 Google 日曆 App，沒裝 App 時 Chrome 會改開網頁版
 *   - 電腦：跳出清單，讓使用者選 Google、Apple、Microsoft 365、Outlook.com
 * 手機另外提供「使用其他行事曆」，給只用 Google 日曆 App 的 iPhone 使用者這類例外。
 *
 * 手機的主按鈕在點擊當下同步開啟，不經過套件：不必多下載套件，也不會因為等載入
 * 超過瀏覽器認定「使用者剛點擊」的時限而被當成彈出視窗擋掉。
 * 只有電腦清單與「使用其他行事曆」才載入 add-to-calendar-button 顯示清單，
 * 清單裡的選項是使用者再點一次，不受時限影響。
 *
 * LINE / Facebook / Instagram 的內建瀏覽器無法處理 .ics，Google 也會擋內嵌瀏覽器登入，
 * 這些情況由畫面引導使用者改用 Safari / Chrome 開啟。
 */
import moment from 'moment';
import Axios from '@/helpers/axios';
import zhTW from '@/helpers/calendar-locale';

const LANGUAGE = 'zh';

export function detectPlatform() {
  const ua = navigator.userAgent || '';
  const isIOS = /iPhone|iPad|iPod/i.test(ua)
    || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  const isLine = /\bLine\//i.test(ua);

  return {
    isIOS,
    isAndroid,
    isMobile: isIOS || isAndroid,
    isLine,
    // Android 的 WebView 會帶 "; wv"，iOS 的 Facebook / Instagram 則沒有 Safari 字樣；
    // LINE 在 iOS 會帶 Safari，所以另外判斷
    inAppBrowser: isLine
      || /FBAN|FBAV|Instagram/i.test(ua)
      || /; ?wv\)/i.test(ua)
      || /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(ua),
  };
}

export function icsUrl(token) {
  return `${Axios.defaults.baseURL}/ics/${token}.ics`;
}

/**
 * 清單的選項：list 是電腦按主按鈕時用的，other 是手機「使用其他行事曆」用的
 */
export function calendarOptions(platform) {
  if (platform.isIOS) return { list: [], other: ['Google', 'Outlook.com'] };
  if (platform.isAndroid) return { list: [], other: ['iCal', 'Outlook.com'] };

  return { list: ['Google', 'Apple', 'Microsoft365', 'Outlook.com'], other: [] };
}

// 後端給的是帶時區的 ISO 時間（2026-10-24T14:30:00+08:00），比較時不受消費者手機的時區影響
export function isUpcoming(calendar) {
  return moment(calendar.start).isAfter();
}

function utcStamp(iso) {
  return moment(iso).utc().format('YYYYMMDD[T]HHmmss[Z]');
}

// 手動組 query string：用 URLSearchParams 會讓 babel 多帶十幾 KB 的 polyfill
export function googleCalendarUrl(calendar) {
  const params = {
    action: 'TEMPLATE',
    text: calendar.title,
    dates: `${utcStamp(calendar.start)}/${utcStamp(calendar.end)}`,
    details: calendar.description,
    location: calendar.location || '',
  };
  const query = Object.keys(params)
    .map((key) => `${key}=${encodeURIComponent(params[key])}`)
    .join('&');

  return `https://calendar.google.com/calendar/render?${query}`;
}

// Android：指定用 Google 日曆 App 開啟，沒裝 App 時 Chrome 會改開 browser_fallback_url（網頁版）
export function googleCalendarIntent(calendar) {
  const webUrl = googleCalendarUrl(calendar);

  return `intent://${webUrl.replace(/^https:\/\//, '')}#Intent;scheme=https;package=com.google.android.calendar;S.browser_fallback_url=${encodeURIComponent(webUrl)};end`;
}

/**
 * 轉成套件要的格式：日期時間用預約當地的時間（parseZone 保留 +08:00 的鐘面時間），
 * 說明欄換行用套件的 [br] 語法
 */
function toLibraryEvent(calendar, token) {
  const start = moment.parseZone(calendar.start);
  const end = moment.parseZone(calendar.end);

  return {
    name: calendar.title,
    description: calendar.description.split('\n').join('[br]'),
    location: calendar.location,
    startDate: start.format('YYYY-MM-DD'),
    startTime: start.format('HH:mm'),
    endDate: end.format('YYYY-MM-DD'),
    endTime: end.format('HH:mm'),
    timeZone: calendar.timezone,
    icsFile: icsUrl(token),
  };
}

let library = null;

// 套件拆成獨立的 chunk（約 100KB），只有要顯示清單時才載入
function loadLibrary() {
  if (!library) {
    // 用 CommonJS 版：ES module 版有 import.meta，webpack 4 解析不了
    library = import(/* webpackChunkName: "calendar" */ 'add-to-calendar-button/dist/commonjs/index')
      .then((module) => {
        const atcb = module.atcb_action ? module : module.default;
        // 套件內建的中文是簡體，換成繁體。註冊後就不會再去 CDN 抓語系檔
        atcb.atcb_register_locale(LANGUAGE, zhTW);
        return atcb;
      })
      .catch((e) => {
        library = null;
        throw e;
      });
  }

  return library;
}

/**
 * 顯示行事曆清單。套件驗證失敗時只會 console.error 並回傳 false，這裡統一改成丟錯，
 * 讓畫面可以改顯示備援的連結
 */
export async function openCalendarList(calendar, token, options, triggerElement) {
  const { atcb_action: atcbAction } = await loadLibrary();
  const opened = await atcbAction({
    ...toLibraryEvent(calendar, token),
    options,
    language: LANGUAGE,
    listStyle: 'modal',
    lightMode: 'light',
    hideBranding: true,
  }, triggerElement);

  if (!opened) throw new Error('add-to-calendar-button 無法開啟清單');
}
